import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { useResource, formatTime } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { InitialsAvatar } from "../components/InitialsAvatar";
import type { Shift } from "../lib/types";

const localDate = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const hoursWorked = (shift: Shift): number | null => {
    if (!shift.clock_in_at || !shift.clock_out_at) return null;
    const minutes = (new Date(shift.clock_out_at).getTime() - new Date(shift.clock_in_at).getTime()) / 60_000
        - (shift.break_minutes ?? 0);
    return Math.max(0, minutes) / 60;
};

/**
 * Best-effort location — attendance is valid without it, just less precise.
 * Accuracy travels with the fix: the server forgives GPS drift up to the
 * reported accuracy before judging the geofence, so an honest weak signal
 * is never punished. The fence flags, it never blocks.
 */
const getLocation = (): Promise<{ lat: number | null; lng: number | null; accuracyM: number | null }> =>
    new Promise((resolve) => {
        if (!navigator.geolocation) {
            resolve({ lat: null, lng: null, accuracyM: null });
            return;
        }
        navigator.geolocation.getCurrentPosition(
            (pos) => resolve({
                lat: pos.coords.latitude, lng: pos.coords.longitude,
                accuracyM: Number.isFinite(pos.coords.accuracy) ? Math.round(pos.coords.accuracy) : null,
            }),
            () => resolve({ lat: null, lng: null, accuracyM: null }),
            { timeout: 6000, enableHighAccuracy: true }
        );
    });

/** The fence verdict as a chip: quiet inside, honest outside, silent when unfenced. */
const fenceChip = (within: boolean | null | undefined, distanceM: number | string | null | undefined) => {
    if (within === true) return <span className="chip pass">At location</span>;
    if (within === false) {
        const metres = Number(distanceM);
        return (
            <span className="chip warn"
                title="Recorded and flagged for review — clocking on is never blocked by GPS.">
                {Number.isFinite(metres) ? `${Math.round(metres)}m from location` : "Outside location"}
            </span>
        );
    }
    return null;
};

/** A worker's own card for today's shift(s) — clock in, clock out, done. */
const MyShiftCard = ({ shift, onChanged }: { shift: Shift; onChanged: () => void }) => {
    const [busy, setBusy] = useState(false);
    const [breakMinutes, setBreakMinutes] = useState("0");
    const [error, setError] = useState<string | null>(null);

    const clockIn = async () => {
        setBusy(true);
        setError(null);
        try {
            const loc = await getLocation();
            await api.post(`/shifts/${shift.id}/clock-in`, loc);
            onChanged();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not clock in");
        } finally {
            setBusy(false);
        }
    };

    const clockOut = async () => {
        setBusy(true);
        setError(null);
        try {
            const loc = await getLocation();
            await api.post(`/shifts/${shift.id}/clock-out`, { ...loc, breakMinutes: Number(breakMinutes) || 0 });
            onChanged();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not clock out");
        } finally {
            setBusy(false);
        }
    };

    const worked = hoursWorked(shift);

    return (
        <div className="card" style={{ marginBottom: "0.85rem" }}>
            <div className="card-body" style={{ display: "flex", alignItems: "center", gap: "1.2rem", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontWeight: 700, fontSize: "1.05rem" }}>
                        {formatTime(shift.starts_at)}–{formatTime(shift.ends_at)}
                    </div>
                    <div style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                        {shift.placement_name ?? "—"} · {shift.role_name ?? "—"}
                    </div>
                </div>

                {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

                {!shift.clock_in_at ? (
                    <button className="btn" disabled={busy} onClick={() => void clockIn()}>
                        {busy ? "Clocking in…" : "Clock in"}
                    </button>
                ) : !shift.clock_out_at ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
                        <span className="chip pass">Clocked in {formatTime(shift.clock_in_at)}</span>
                        {fenceChip(shift.clock_in_within_fence, shift.clock_in_distance_m)}
                        <div className="field" style={{ width: 110 }}>
                            <label>Break (min)</label>
                            <input type="number" min={0} value={breakMinutes}
                                onChange={(e) => setBreakMinutes(e.target.value)} />
                        </div>
                        <button className="btn" disabled={busy} onClick={() => void clockOut()}>
                            {busy ? "Clocking out…" : "Clock out"}
                        </button>
                    </div>
                ) : (
                    <div style={{ textAlign: "right" }}>
                        <span className="chip mute">
                            {formatTime(shift.clock_in_at)}–{formatTime(shift.clock_out_at)}
                        </span>
                        {fenceChip(shift.clock_in_within_fence, shift.clock_in_distance_m)}
                        {fenceChip(shift.clock_out_within_fence, shift.clock_out_distance_m)}
                        <div style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: 3 }}>
                            {worked !== null ? `${worked.toFixed(1)} hrs worked` : "—"}
                            {shift.verified_at && " · verified"}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

/** Manager view: everyone's attendance for today, with a sign-off action. */
const TodaysAttendance = ({ shifts, onChanged }: { shifts: Shift[]; onChanged: () => void }) => {
    const [busy, setBusy] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const verify = async (shift: Shift) => {
        setBusy(shift.id);
        setError(null);
        try {
            await api.post(`/shifts/${shift.id}/verify-actual`, {});
            onChanged();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Could not verify");
        } finally {
            setBusy(null);
        }
    };

    const rostered = shifts.filter((s) => s.status !== "cancelled" && s.status !== "open");

    return (
        <div className="card">
            <div className="card-head"><h2>Today&rsquo;s attendance</h2></div>
            <div className="card-body flush">
                {error && <div className="error-banner" style={{ margin: "0.75rem" }}>{error}</div>}
                {rostered.length === 0 ? (
                    <div className="empty">No shifts rostered today.</div>
                ) : (
                    <div className="table-wrap">
                        <table>
                            <thead>
                                <tr>
                                    <th>Worker</th><th>Rostered</th><th>Clock in</th><th>Clock out</th>
                                    <th>Break</th><th>Worked</th><th style={{ textAlign: "right" }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rostered.map((shift) => {
                                    const worked = hoursWorked(shift);
                                    return (
                                        <tr key={shift.id}
                                            className={shift.employee_id ? "clickable" : undefined}
                                            onClick={() => { if (shift.employee_id) window.location.assign(`/employees?id=${shift.employee_id}`); }}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                                    <InitialsAvatar name={shift.employee_name ?? "Unassigned"} size={24} />
                                                    {shift.employee_name ?? "Unassigned"}
                                                </div>
                                            </td>
                                            <td className="mono">{formatTime(shift.starts_at)}–{formatTime(shift.ends_at)}</td>
                                            <td className="mono">{shift.clock_in_at ? formatTime(shift.clock_in_at) : "—"}</td>
                                            <td className="mono">{shift.clock_out_at ? formatTime(shift.clock_out_at) : "—"}</td>
                                            <td className="mono">{shift.break_minutes ? `${shift.break_minutes}m` : "—"}</td>
                                            <td className="mono">{worked !== null ? `${worked.toFixed(1)}h` : "—"}</td>
                                            <td style={{ textAlign: "right" }}>
                                                {shift.verified_at ? (
                                                    <span className="chip pass">Verified</span>
                                                ) : shift.clock_out_at ? (
                                                    <button className="icon-btn" disabled={busy === shift.id}
                                                        onClick={() => void verify(shift)}>
                                                        {busy === shift.id ? "Verifying…" : "Verify"}
                                                    </button>
                                                ) : shift.clock_in_at ? (
                                                    <span className="chip warn">In progress</span>
                                                ) : (
                                                    <span className="chip mute">Not started</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export const TimeClock = () => {
    const { user } = useAuth();
    const isStaff = user?.role === "staff";
    const canSupervise = user?.role === "admin" || user?.role === "manager" || user?.role === "supervisor";

    const today = localDate(new Date());
    const shifts = useResource<Shift[]>(`/shifts?from=${today}T00:00:00Z&to=${today}T23:59:59Z`);

    const reload = () => shifts.reload();

    const myShifts = (shifts.data ?? [])
        .filter((s) => s.employee_id === user?.employeeId && s.status !== "cancelled")
        .sort((a, b) => a.starts_at.localeCompare(b.starts_at));

    if (shifts.error) return <div className="error-banner">{shifts.error}</div>;
    if (shifts.loading) return <div className="empty">Loading…</div>;

    return (
        <div>
            {(isStaff || user?.employeeId) && (
                <>
                    {myShifts.length === 0 ? (
                        <div className="card" style={{ marginBottom: "0.85rem" }}>
                            <div className="card-body empty">No shift rostered for you today.</div>
                        </div>
                    ) : (
                        myShifts.map((shift) => <MyShiftCard key={shift.id} shift={shift} onChanged={reload} />)
                    )}
                </>
            )}

            {canSupervise && <TodaysAttendance shifts={shifts.data ?? []} onChanged={reload} />}
        </div>
    );
};
